import {
  CARE_REFERRAL_CATEGORIES,
  REFERRAL_TABS,
  MEDICAL_COVERAGE_TYPES,
  getCareReferralCategoryConfig
} from '../../../constants/careReferralCategories';

const TAB_HOSPITAL = CARE_REFERRAL_CATEGORIES.HOSPITAL_REFERRAL.id;
const TAB_OPHTHALMO = CARE_REFERRAL_CATEGORIES.OPHTHALMOLOGY.id;
const TAB_PEDIATRICS = CARE_REFERRAL_CATEGORIES.PEDIATRICS.id;
const TAB_OPTICAL = CARE_REFERRAL_CATEGORIES.OPTICAL.id;

const CLINICAL_KEYS = {
  [TAB_HOSPITAL]: 'clinical_information_hospital',
  [TAB_OPHTHALMO]: 'clinical_information_ophthalmology',
  [TAB_PEDIATRICS]: 'clinical_information_pediatrics',
  [TAB_OPTICAL]: 'clinical_information_optical'
};

export function normalizeCategory(category) {
  return String(category || '').toUpperCase();
}

export function matchesCategory(lineCategory, tabCategory) {
  return normalizeCategory(lineCategory) === normalizeCategory(tabCategory);
}

export function coverageLabel(id) {
  return MEDICAL_COVERAGE_TYPES[id]?.label || id || '—';
}

export function referralMetaLine(referral) {
  const parts = [];
  if (referral.partner_name) parts.push(referral.partner_name);
  const meta = referral.metadata || {};
  const catalogMeta = referral.catalog_metadata || {};
  const config = getCareReferralCategoryConfig(referral.category);
  for (const field of config?.metadataFields || []) {
    const v = meta[field.key] ?? catalogMeta[field.key];
    if (v) parts.push(v);
  }
  if (referral.category === TAB_OPTICAL) {
    const od = [meta.od_sphere, meta.od_cylinder, meta.od_axis].filter(Boolean).join('/');
    const og = [meta.og_sphere, meta.og_cylinder, meta.og_axis].filter(Boolean).join('/');
    if (od) parts.push(`OD ${od}`);
    if (og) parts.push(`OG ${og}`);
  }
  return parts.join(' · ');
}

export function mapReferralToLine(referral) {
  return {
    catalog_referral_id: referral.catalog_referral_id,
    referral_name: referral.referral_name,
    category: normalizeCategory(referral.category),
    coverage_type: referral.coverage_type,
    partner_id: referral.partner_id,
    partner_name: referral.partner_name,
    metadata: referral.metadata || {},
    catalog_metadata: referral.catalog_metadata || {}
  };
}

export function parseCareReferralsResponse(res) {
  if (!res?.success) {
    return {
      referrals: [],
      clinical_information_hospital: '',
      clinical_information_ophthalmology: '',
      clinical_information_pediatrics: '',
      clinical_information_optical: ''
    };
  }
  const data = res.data;
  if (Array.isArray(data)) {
    return {
      referrals: data.map(mapReferralToLine),
      clinical_information_hospital: '',
      clinical_information_ophthalmology: '',
      clinical_information_pediatrics: '',
      clinical_information_optical: ''
    };
  }
  return {
    referrals: Array.isArray(data?.referrals) ? data.referrals.map(mapReferralToLine) : [],
    clinical_information_hospital: data?.clinical_information_hospital || '',
    clinical_information_ophthalmology: data?.clinical_information_ophthalmology || '',
    clinical_information_pediatrics: data?.clinical_information_pediatrics || '',
    clinical_information_optical: data?.clinical_information_optical || ''
  };
}

export function getClinicalInformationForCategory(parsed, category) {
  const key = CLINICAL_KEYS[normalizeCategory(category)];
  return key ? parsed[key] || '' : '';
}

export function getClinicalStateKey(category) {
  return CLINICAL_KEYS[normalizeCategory(category)] || CLINICAL_KEYS[TAB_HOSPITAL];
}

export function getCategoryLabelPlural(category) {
  return getCareReferralCategoryConfig(category)?.labelPlural || 'Orientations';
}

export function getClinicalPlaceholder(category) {
  const id = normalizeCategory(category);
  if (id === TAB_HOSPITAL) return 'Motif d\'orientation hospitalière…';
  if (id === TAB_OPHTHALMO) return 'Contexte clinique pour l\'ophtalmologie…';
  if (id === TAB_PEDIATRICS) return 'Contexte clinique pour la pédiatrie…';
  if (id === TAB_OPTICAL) return 'Contexte clinique pour la prise en charge lunettes…';
  return 'Renseignement clinique…';
}

export function getClinicalHint(category) {
  const label = getCategoryLabelPlural(category).toLowerCase();
  return `Propre à cet onglet — affiché sur le bon de ${label}.`;
}

export function getViewPrintLabel(category) {
  return getCategoryLabelPlural(category).toLowerCase();
}

export function getTabEmptyMessage(category, saved = false) {
  const label = getCategoryLabelPlural(category).toLowerCase();
  const singular = label.endsWith('s') ? label.slice(0, -1) : label;
  if (saved) {
    return `Aucune ${singular} enregistrée pour cette consultation.`;
  }
  return `Aucune ${singular} dans la demande — ajoutez un acte du catalogue.`;
}

export { REFERRAL_TABS, TAB_HOSPITAL, TAB_OPHTHALMO, TAB_PEDIATRICS, TAB_OPTICAL };

export function formatConsultationDate(consultation) {
  const raw = consultation?.consultation_date || consultation?.created_at;
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' });
  } catch {
    return String(raw);
  }
}

export function formatPrintDate() {
  return new Date().toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' });
}
